// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pace/App/Components/Common/SectionWrapper";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import DataTable, { DataTablePresentationProps, DataTableProps } from "@pace/MobiusOverrides/DataTable/DataTable";
import DataTableHeader, { DataTableHeaderProps } from "@pace/MobiusOverrides/DataTable/DataTableHeader";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@pace/Overrides/Components/QuoteSummaryCard";
import * as React from "react";
import { ResolveThunks, connect } from "react-redux";

// XNG-Changes
const enum fields {
    numberOfQuotes = "numberOfQuotes",
    sectionTitle = "sectionTitle",
    linkText = "linkText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfQuotes]: number;
        [fields.sectionTitle]: string;
        [fields.linkText]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes:
            isAuthenticated &&
            (session.userRoles || "").indexOf("Requisitioner") === -1 &&
            // XNG-Changes
            !xHasRoles(state, ["CannotViewQuotes"]),
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    // noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    sectionWrapperStyles?: SectionWrapperStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    quoteNumberTableHeader?: DataTableHeaderProps;
    statusTableHeader?: DataTableHeaderProps;
    jobNameTableHeader?: DataTableHeaderProps;
    dateTableHeader?: DataTableHeaderProps;
    qtyTableHeader?: DataTableHeaderProps;
    totalTableHeader?: DataTableHeaderProps;
    actionTableHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    tableWrapper?: GridItemProps;
    tableStyle?: DataTablePresentationProps;
    noQuotesGridItem?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    // noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfQuotes || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            // XNG-Changes: remove OOTB CardList
            <SectionWrapper
                title={this.props.fields.sectionTitle}
                titleVariant="small"
                extendedStyles={styles.sectionWrapperStyles}
                link={myQuotesPageUrl}
                linkText={this.props.fields.linkText}
                data-test-selector="RecentQuotessWidget"
            >
                {quotesDataView.value.length === 0 && (
                    <StyledWrapper {...styles.noQuotesGridItem}>
                        <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                    </StyledWrapper>
                )}
                {quotesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.quoteNumberTableHeader}>{translate("Quote #")}</DataTableHeader>
                            <DataTableHeader {...styles.statusTableHeader}>{translate("Status")}</DataTableHeader>
                            <DataTableHeader {...styles.jobNameTableHeader}>{translate("Job Name")}</DataTableHeader>
                            <DataTableHeader {...styles.dateTableHeader}>{translate("Date")}</DataTableHeader>
                            <DataTableHeader {...styles.qtyTableHeader}>{translate("QTY")}</DataTableHeader>
                            <DataTableHeader {...styles.totalTableHeader}>{translate("Total")}</DataTableHeader>
                            <DataTableHeader {...styles.actionTableHeader}>{translate("Action")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {quotesDataView.value.map(quote => (
                                <QuoteSummaryCard
                                    key={quote.id}
                                    quote={quote}
                                    session={session}
                                    quoteSettings={settingsCollection.quoteSettings}
                                    extendedStyles={styles.quoteSummaryCard}
                                />
                            ))}
                        </DataTableBody>
                    </DataTable>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.numberOfQuotes,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.sectionTitle,
                displayName: "Section Title",
                editorTemplate: "TextField",
                defaultValue: "Quotes",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "View All Quotes",
                fieldType: "Translatable",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
